/**
 * Screen wake lock.
 *
 * A guest holding their pass at a club counter cannot tap the screen every 30
 * seconds — if the phone sleeps, the QR they are showing a bouncer disappears.
 * The Screen Wake Lock API holds the display on while the pass is visible.
 *
 * This is progressive enhancement and NOT a brightness API: no browser exposes
 * programmatic screen brightness (iOS Safari not at all, Android Chrome only
 * through the wake lock), which is why the QR itself is rendered black-on-white
 * inset on a white tile and offers a full-screen mode. If the lock is
 * unsupported or refused, everything still works — the screen just may dim.
 */

type Sentinel = { released: boolean; release: () => Promise<void> }

type WakeLockCapable = Navigator & {
  wakeLock?: { request: (type: 'screen') => Promise<Sentinel> }
}

/** True where the browser exposes the Screen Wake Lock API at all. */
export function wakeLockSupported(): boolean {
  return typeof navigator !== 'undefined' && !!(navigator as WakeLockCapable).wakeLock
}

/** Human-readable reason the wake lock is unavailable, for the UI hint. */
export function wakeLockUnavailableReason(): string {
  if (typeof navigator === 'undefined') return 'unavailable in this environment'
  if (!wakeLockSupported()) return 'your browser does not support it'
  return 'unavailable'
}

/**
 * Hold the screen on and return a release function.
 *
 * Resolution is deliberately tolerant: on a non-secure origin, in an unsupported
 * browser, or when the page is hidden the request rejects — that is an expected
 * outcome, not an error, so this never throws. Re-acquires on `visibilitychange`
 * because the browser silently drops the lock when the tab is backgrounded
 * (which is also what happens when the guest opens their camera).
 */
export async function holdScreenAwake(): Promise<() => void> {
  const nav = typeof navigator === 'undefined' ? null : (navigator as WakeLockCapable)
  if (!nav?.wakeLock) return () => {}
  const wakeLock = nav.wakeLock

  let sentinel: Sentinel | null = null
  let released = false

  const acquire = async () => {
    if (released) return
    try {
      sentinel = await wakeLock.request('screen')
    } catch {
      sentinel = null
    }
  }

  const onVisibility = () => {
    if (document.visibilityState === 'visible') void acquire()
  }

  await acquire()
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility)

  return () => {
    released = true
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility)
    void sentinel?.release?.().catch(() => {})
    sentinel = null
  }
}
